/** Live unrealized MTM for an open positional basket.
 *
 * The stored `lastMtm` column is written once a day by the 15:25 IST MTM
 * job, so any surface that shows it alone is up to 24 hours stale — which
 * is exactly why the Trade Log and the Strategy page used to disagree
 * (operator, 2026-08-07). Both now call THIS, and fall back to the
 * snapshot only when it cannot be computed.
 *
 * The formula is the frontend twin of `basket_math.unrealized_mtm`:
 *
 *     MTM = Σ dir·LTP·qty − net entry cost,      dir = +1 BUY / −1 SELL
 *
 * Null (→ snapshot fallback) while the basket is flat, before the first
 * quotes land, or when the broker session is missing.
 */

import { useQuery } from "@tanstack/react-query";
import { brokerApi } from "@/lib/brokerApi";
import type { TradeLegEntry, TradeLogEntry } from "@/types/tradeLog";

/** Live leg-LTP poll — THE number the operator watches on an open
 * basket, so faster than the trade refetch. */
export const LTP_POLL_MS = 5_000;

/** Kite LTP wants "EXCHANGE:TRADINGSYMBOL" — SENSEX contracts trade on
 * BFO, NIFTY on NFO. */
export function ltpSymbol(contractSymbol: string): string {
  return `${contractSymbol.startsWith("SENSEX") ? "BFO" : "NFO"}:${contractSymbol}`;
}

function direction(leg: TradeLegEntry): number {
  return leg.side === "BUY" ? 1 : -1;
}

export function useLiveMtm(basket: TradeLogEntry): number | null {
  const legs = basket.legs ?? [];
  const q = useQuery({
    queryKey: ["basket-live-mtm", basket.id] as const,
    queryFn: async () => {
      const quotes = await Promise.all(legs.map((l) => brokerApi.ltp(ltpSymbol(l.contractSymbol))));
      const value = legs.reduce(
        (total, l, i) => total + direction(l) * (quotes[i]?.last_price ?? NaN) * l.quantity,
        0,
      );
      const mtmNow = value - (basket.netEntryCost ?? NaN);
      if (!Number.isFinite(mtmNow)) throw new Error("leg quote missing");
      return Math.round(mtmNow * 100) / 100;
    },
    enabled: basket.exitReason === "OPEN" && legs.length > 0 && basket.netEntryCost !== null,
    refetchInterval: LTP_POLL_MS,
  });
  return q.data ?? null;
}

/** Live unrealized MTM for an OPEN intraday trade — the same 5s LTP poll as a
 * basket (operator, 2026-09-15: "Like this which we show in postional trade").
 * Every intraday engine BUYS its option, so
 *
 *     MTM = (LTP − entry premium) · quantity
 *
 * Null while the trade is not open, a field is missing, or no quote landed.
 * There is no stored snapshot to fall back on for an intraday trade.
 */
export function useLiveTradeMtm(trade: TradeLogEntry): number | null {
  const { contractSymbol, entryPremium, quantity } = trade;
  const q = useQuery({
    queryKey: ["trade-live-mtm", trade.id] as const,
    queryFn: async () => {
      const quote = await brokerApi.ltp(ltpSymbol(contractSymbol ?? ""));
      const mtmNow = (quote.last_price - (entryPremium ?? NaN)) * (quantity ?? NaN);
      if (!Number.isFinite(mtmNow)) throw new Error("quote missing");
      return Math.round(mtmNow * 100) / 100;
    },
    enabled:
      trade.exitReason === "OPEN" &&
      contractSymbol !== null &&
      entryPremium !== null &&
      quantity !== null,
    refetchInterval: LTP_POLL_MS,
  });
  return q.data ?? null;
}
